import React from 'react';
import { Link } from 'react-router-dom';
import CompressPDF from '../components/CompressPDF';
import Seo from '../components/seo/Seo';
import { softwareAppSchema, howToSchema, breadcrumbSchema } from '../components/seo/jsonld';
import GoogleAd from '../components/GoogleAd';
import { AD_SLOTS } from '../components/adSlots';

const PATH = '/compress-pdf';
const TITLE = 'Compress PDF Online — Reduce File Size Free';
const DESCRIPTION = 'Shrink PDF file size in your browser. No uploads, no email, no quality loss on text. Works for email attachments, upload limits, and slow mobile connections.';

const CompressPdfPage = () => (
    <>
        <Seo
            title={TITLE}
            description={DESCRIPTION}
            path={PATH}
            jsonLd={[
                softwareAppSchema({ name: 'Compress PDF', description: DESCRIPTION, path: PATH }),
                howToSchema({
                    name: 'How to compress a PDF',
                    description: 'Reduce a PDF file size in the browser without uploading the file to a server.',
                    steps: [
                        'Pick a PDF from your device.',
                        'Review the original file size shown by the tool.',
                        'Click "Compress PDF".',
                        'The compressed PDF downloads automatically with the new size and reduction percentage shown.',
                    ],
                }),
                breadcrumbSchema([
                    { name: 'Home', path: '/' },
                    { name: 'Compress PDF', path: PATH },
                ]),
            ]}
        />

        <div className="page-container">
            <article>
                <header className="page-header">
                    <h1>Compress PDF</h1>
                    <p className="page-lead">
                        Reduce the size of a PDF so it fits under an email attachment limit or
                        an upload cap — without sending the file to a server.
                    </p>
                </header>

                <section className="tool-embed">
                    <CompressPDF />
                </section>

                <GoogleAd slot={AD_SLOTS.COMPRESS_PDF} className="ad-slot-inline" />

                <section className="content-section">
                    <h2>Why PDFs get so large in the first place</h2>
                    <p>
                        A PDF is a container format — it can hold vector text, raster images,
                        fonts, embedded attachments, JavaScript, form widgets, and digital
                        signatures. In practice, most of the file size usually comes from two
                        places:
                    </p>
                    <ul>
                        <li>
                            <strong>Embedded images.</strong> A 20-page PDF with a scanned
                            photograph on each page can easily be 50&nbsp;MB. The images dominate
                            everything else.
                        </li>
                        <li>
                            <strong>Redundant or unreferenced objects.</strong> PDFs that have
                            been edited multiple times accumulate dead objects that were never
                            cleaned up.
                        </li>
                    </ul>
                    <p>
                        This tool targets the second category. It rewrites the PDF's internal
                        structure, deduplicates streams, and drops unreferenced objects. For
                        text-heavy PDFs, you can often shave off a meaningful chunk — sometimes
                        30% or more — without touching any visible content.
                    </p>

                    <h2>What kinds of PDFs benefit most</h2>
                    <ul>
                        <li>
                            <strong>Generated reports from business software</strong> (accounting,
                            CRM, BI tools) — these are often saved with bloated structures.
                        </li>
                        <li>
                            <strong>Merged PDFs</strong> where multiple documents have been
                            combined — merging tools sometimes leave behind duplicated fonts
                            and resources.
                        </li>
                        <li>
                            <strong>PDFs exported from Word or Google Docs</strong> with many
                            iterations of edits.
                        </li>
                    </ul>
                    <p>
                        PDFs that are mostly high-resolution scanned images will not shrink much
                        with this tool. For those, you'd need an aggressive image-recompression
                        pass (lossy downsampling), which risks readability. We intentionally
                        don't do that here because it's easy to silently destroy quality.
                    </p>

                    <h2>Lossless vs. lossy: what this tool does</h2>
                    <p>
                        This compressor is lossless by design — it does not resample images,
                        convert colour spaces, or alter any visible content. Text stays crisp,
                        images stay at their original resolution. The tradeoff is that
                        compression ratios are modest compared to tools that do lossy image
                        compression. If you need aggressive size reduction and can tolerate
                        image-quality loss, a desktop tool like Acrobat Pro or a command-line
                        utility like <code>gs</code> (Ghostscript) is a better fit.
                    </p>

                    <h2>How to compress a PDF on this page</h2>
                    <ol>
                        <li>Pick a PDF from your device.</li>
                        <li>The tool shows the original file size.</li>
                        <li>Click <strong>Compress PDF</strong>.</li>
                        <li>
                            The compressed PDF downloads automatically. You'll see the new size
                            and the reduction percentage.
                        </li>
                    </ol>

                    <h2>Tips for getting email under the limit</h2>
                    <p>
                        Most email providers cap attachments at 25&nbsp;MB. If your PDF is larger
                        than that after compression, consider these options:
                    </p>
                    <ul>
                        <li>Split the PDF into smaller batches (e.g., first 20 pages, next 20).</li>
                        <li>Upload to a file-sharing link (Google Drive, Dropbox) and email the link.</li>
                        <li>
                            If the document is mostly scans, a desktop tool like Adobe Acrobat or
                            macOS Preview's &quot;Reduce File Size&quot; filter can apply lossy
                            image compression — at the cost of visual quality.
                        </li>
                    </ul>
                </section>

                <section className="content-section">
                    <h2>Related tools</h2>
                    <ul>
                        <li><Link to="/word-to-pdf">Word to PDF</Link></li>
                        <li><Link to="/image-to-pdf">Image to PDF</Link></li>
                        <li><Link to="/pdf-to-image">PDF to Image</Link></li>
                    </ul>
                </section>
            </article>
        </div>
    </>
);

export default CompressPdfPage;
